import React, { useMemo, useState } from 'react';

/**
 * Interactive retrieval demo.
 * User picks a query → we show the pack MME would build, token budget filling up.
 */
function Demo() {
  const memories = useMemo(
    () => [
      {
        id: 'm1',
        text: 'User prefers TypeScript over JavaScript for all new services.',
        tags: ['preference', 'typescript', 'coding-style'],
        tokens: 18,
        score: 0.92,
      },
      {
        id: 'm2',
        text: "Decided against Redis for the auth service — using in-memory LRU instead.",
        tags: ['auth', 'redis', 'decision', 'architecture'],
        tokens: 22,
        score: 0.88,
      },
      {
        id: 'm3',
        text: "Postgres connection string lives in 1Password vault 'Backend / Prod'.",
        tags: ['postgres', 'secrets', 'ops'],
        tokens: 20,
        score: 0.81,
      },
      {
        id: 'm4',
        text: 'OAuth2 PKCE flow is required for all public clients per security review.',
        tags: ['auth', 'oauth2', 'pkce', 'security'],
        tokens: 24,
        score: 0.94,
      },
      {
        id: 'm5',
        text: 'Morning standup is 9:15am Tuesdays and Thursdays only.',
        tags: ['meeting', 'schedule'],
        tokens: 14,
        score: 0.22,
      },
      {
        id: 'm6',
        text: 'Tests must use Vitest — Jest is deprecated in this monorepo.',
        tags: ['testing', 'vitest', 'coding-style'],
        tokens: 16,
        score: 0.71,
      },
      {
        id: 'm7',
        text: 'JWT refresh tokens rotate every 7 days; access tokens 15 min.',
        tags: ['auth', 'jwt', 'refresh-tok'],
        tokens: 18,
        score: 0.9,
      },
      {
        id: 'm8',
        text: 'Favorite coffee shop: Prufrock. User works there Fridays.',
        tags: ['personal', 'schedule'],
        tokens: 14,
        score: 0.08,
      },
    ],
    []
  );

  const queries = useMemo(
    () => [
      {
        id: 'q1',
        label: 'How should I implement auth?',
        tags: ['auth', 'oauth2', 'jwt', 'coding-style'],
      },
      {
        id: 'q2',
        label: "What's our testing setup?",
        tags: ['testing', 'coding-style'],
      },
      {
        id: 'q3',
        label: 'Where are the prod secrets?',
        tags: ['secrets', 'ops', 'postgres'],
      },
    ],
    []
  );

  const [queryId, setQueryId] = useState('q1');
  const [budget, setBudget] = useState(64);

  const query = queries.find((q) => q.id === queryId);

  // Rank memories: score each by tag overlap with query.
  const ranked = useMemo(() => {
    return memories
      .map((m) => {
        const hits = m.tags.filter((t) => query.tags.includes(t));
        const overlap = hits.length / Math.max(query.tags.length, 1);
        const combined = 0.6 * m.score + 0.4 * overlap;
        return { ...m, hits, combined };
      })
      .filter((m) => m.combined > 0.25)
      .sort((a, b) => b.combined - a.combined);
  }, [memories, query]);

  // Fill up to budget.
  const pack = [];
  let used = 0;
  for (const m of ranked) {
    if (used + m.tokens > budget) continue;
    pack.push(m);
    used += m.tokens;
  }

  const pct = Math.min(100, Math.round((used / budget) * 100));

  return (
    <div className="demo">
      {/* LEFT: controls */}
      <div className="demo-left">
        <div>
          <div className="demo-label">Try a query</div>
          <div className="query-chips mt-8">
            {queries.map((q) => (
              <button
                key={q.id}
                className={`chip ${q.id === queryId ? 'on' : ''}`}
                onClick={() => setQueryId(q.id)}
                type="button"
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="demo-label">Extracted seed tags</div>
          <div className="query-chips mt-8">
            {query.tags.map((t) => (
              <span key={t} className="chip on" style={{ cursor: 'default' }}>
                {t}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center">
            <span className="demo-label">Token budget</span>
            <span
              className="mono"
              style={{ fontSize: 11, color: 'var(--ink-3)' }}
            >
              {used} / {budget}
            </span>
          </div>
          <input
            type="range"
            min="32"
            max="128"
            step="8"
            value={budget}
            onChange={(e) => setBudget(+e.target.value)}
            style={{ width: '100%', marginTop: 8, accentColor: 'var(--ink)' }}
          />
          <div className="budget-bar">
            <div style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div
          style={{
            marginTop: 'auto',
            fontSize: 12,
            color: 'var(--ink-4)',
            lineHeight: 1.5,
          }}
        >
          MME ranks by{' '}
          <span className="mono">
            activation × recency × importance − diversity
          </span>
          , then packs items in score order until the budget fills.
        </div>
      </div>

      {/* RIGHT: resulting pack */}
      <div className="demo-right">
        <div
          className="flex justify-between items-center"
          style={{ marginBottom: 12 }}
        >
          <span className="demo-label">
            Memory pack ({pack.length} items)
          </span>
          <span
            className="mono"
            style={{ fontSize: 11, color: 'var(--ink-4)' }}
          >
            POST /memory/inject
          </span>
        </div>

        <div>
          {pack.map((m, i) => (
            <div
              key={m.id + queryId + budget + i}
              className="pack-item"
            >
              <div>{m.text}</div>
              <div className="pack-score">{m.combined.toFixed(2)}</div>
              <div className="pack-tags">
                {m.tags.map((t) => (
                  <span
                    key={t}
                    className={`t ${m.hits.includes(t) ? 'hit' : ''}`}
                  >
                    {t}
                  </span>
                ))}
                <span style={{ marginLeft: 6, opacity: 0.6 }}>
                  · {m.tokens}t
                </span>
              </div>
            </div>
          ))}
          {pack.length === 0 && (
            <div
              style={{
                color: 'var(--ink-4)',
                fontSize: 13,
                padding: '24px 0',
              }}
            >
              No memories fit the budget. Try increasing the budget.
            </div>
          )}
        </div>

        {ranked.length > pack.length && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-4)',
              marginTop: 8,
              padding: '10px 12px',
              border: '1px dashed var(--line-2)',
              borderRadius: 6,
            }}
          >
            <span className="mono" style={{ color: 'var(--ink-3)' }}>
              {ranked.length - pack.length}
            </span>{' '}
            more relevant memories available — skipped because the budget is
            full.
          </div>
        )}
      </div>
    </div>
  );
}

export default Demo;
